import { UnmigratedReviewResult } from "@swivl-mono/api-helper";
import { Button, ButtonVariant, Spinner } from "@swivl-mono/ui";
import { Link } from "react-router-dom";

/** Where this page's automatic save is. `retry` says what Retry re-runs: the whole page, or only the save. */
export type RunSaveState =
  | { status: "loading" }
  | { status: "saving" }
  | { status: "saved"; result: UnmigratedReviewResult }
  | { status: "failed"; reason: string; retry: "reload" | "save" };

interface UnmigratedRunCalloutProps {
  state: RunSaveState;
  onRetry: () => void;
}

function savedMessage(u: UnmigratedReviewResult): string {
  if (u.ticketNumber == null) return `${u.totalCount} archived, nothing actionable for CX.`;
  const ticketState = u.created
    ? `opened with ${u.taskCount} task(s)`
    : u.tasksAdded > 0
      ? `updated with ${u.tasksAdded} new task(s)`
      : "already up to date";
  return `${u.actionableCount} of ${u.totalCount} actionable. CX review ticket ${ticketState}:`;
}

/**
 * The run is archived and the CX ticket synced automatically once every suggestion has loaded, since
 * only this page produces them. This tells the operator to let it finish, and what the save did.
 */
export function UnmigratedRunCallout({ state, onRetry }: UnmigratedRunCalloutProps): React.ReactElement {
  if (state.status === "loading" || state.status === "saving") {
    return (
      <div className="body-sm mb-4 flex items-center gap-2 rounded border border-yellow-200 bg-yellow-50 px-4 py-3 text-yellow-700">
        <Spinner />
        {state.status === "loading"
          ? "Suggestions are still loading. Keep this page open until they finish so this run is saved and the CX review ticket is updated."
          : "Saving this run and updating the CX review ticket…"}
      </div>
    );
  }
  if (state.status === "failed") {
    return (
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded border border-red-200 bg-red-50 px-4 py-3">
        <span className="body-sm text-red-700">This run wasn&apos;t saved: {state.reason}</span>
        <Button variant={ButtonVariant.GHOST} onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }
  const u = state.result;
  return (
    <div className="mb-4 rounded border border-teal-200 bg-teal-50 px-4 py-3">
      <p className="body-sm text-teal-900">
        Run saved. {savedMessage(u)}
        {u.ticketNumber != null && (
          <>
            {" "}
            <Link to={`/ticket/${u.ticketNumber}`} className="text-orange-500 underline">
              #{u.ticketNumber}
            </Link>
          </>
        )}
      </p>
      {u.archiveKey === null && <p className="body-xs mt-1 text-yellow-700">The run could not be archived to S3. The review ticket is unaffected.</p>}
    </div>
  );
}
